import type { SupportedChainName } from "@src/components/DefuseSDK/types/base"
import { validateAddress } from "@src/components/DefuseSDK/utils/validateAddress"

/**
 * Address shapes Defuse cannot currently transact to, but that are
 * recognizable enough that we'd rather tell the user "we don't support this
 * format" than the generic "couldn't read a wallet address" message.
 *
 * Add an entry here when:
 *   - The format has a distinguishing prefix and length that won't
 *     false-match other chains' addresses.
 *   - Defuse's underlying `validateAddress` rejects it (so the supported
 *     check above won't claim it).
 */
const KNOWN_UNSUPPORTED_FORMATS: ReadonlyArray<{
  pattern: RegExp
  name: string
}> = [
  // Zcash Sapling shielded (`zs1…`). `validateZcashAddress` accepts
  // transparent (`t1` / `t3`), TEX (`tex1`), and Unified (`u1`) — Sapling
  // shielded is the one Zcash address family we genuinely can't transact to.
  { pattern: /^zs1[a-z0-9]{60,}$/i, name: "Zcash shielded" },
]

export type ScanClassification =
  | { kind: "supported" }
  | { kind: "recognized-unsupported"; formatName: string }
  | { kind: "unrecognized" }

/**
 * Classifies a scanned address so the contact-modal scan handler can decide
 * whether to fill the field or reject with which message.
 *
 * - `supported` — at least one of the given chain validators accepts the
 *   address. Caller should fill the field and run `detectNetworkFromAddress`
 *   for auto-network-select.
 * - `recognized-unsupported` — the address matches a known crypto-address
 *   format Defuse can't currently transact to (e.g. Zcash shielded `zs1…`).
 *   Caller should refuse with a format-aware message.
 * - `unrecognized` — neither validators nor known-unsupported patterns
 *   match. Caller should refuse with the generic "couldn't read" message.
 *
 * Pure: no I/O, no on-chain RPC.
 */
export function classifyScannedAddress(
  address: string,
  availableChains: readonly SupportedChainName[]
): ScanClassification {
  const trimmed = address.trim()
  if (trimmed.length === 0) return { kind: "unrecognized" }

  for (const chain of availableChains) {
    // NEAR's underlying validator (`utils.validateNearAddress`) is too
    // permissive for raw QR classification — it accepts any lowercase
    // alphanumeric+`-_` string, including obvious garbage like
    // `helloworld`, `not-an-address`, `abc_def`, or short bech32-looking
    // fragments like `bc1abc`. Without a gate, the classifier would call
    // such inputs `supported` and the modal would fill the field with
    // garbage. Mirror the detector's stricter dotted-name predicate so
    // only proper NEAR named accounts qualify here. 64-hex implicit
    // accounts are still caught by other chain validators (Sui accepts
    // bare 64-hex), which keeps the existing "ambiguous → fill, network
    // empty" behaviour for those shapes.
    if (chain === "near") {
      if (
        /^[a-z\d]+([-_][a-z\d]+)*(\.[a-z\d]+([-_][a-z\d]+)*)+$/.test(trimmed) &&
        validateAddress(trimmed, "near")
      ) {
        return { kind: "supported" }
      }
      continue
    }
    if (validateAddress(trimmed, chain)) {
      return { kind: "supported" }
    }
  }

  for (const { pattern, name } of KNOWN_UNSUPPORTED_FORMATS) {
    if (pattern.test(trimmed)) {
      return { kind: "recognized-unsupported", formatName: name }
    }
  }

  return { kind: "unrecognized" }
}
